import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocale } from '@/i18n/LocaleContext';

const STEP_PROPS = [
  '/assets/04-props/yellow-block.png',
  '/assets/04-props/gold-chain-link.png',
  '/assets/04-props/xaut-gold-bar.png',
];

export const GoldRoom = () => {
  const { t } = useLocale();
  const reduced = useReducedMotion();
  const g = t.goldRoom;

  const rise = (delay = 0) => ({
    initial: reduced ? {} : { opacity: 0, y: 28 },
    whileInView: reduced ? {} : { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section id="gold" data-testid="goldroom-section" className="relative py-20 sm:py-28 border-t-4 border-gold overflow-hidden">
      {/* Heavier gold texture: this room is about weight */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{ backgroundImage: 'url(/assets/04-backgrounds/gold-bars-pattern-seamless.svg)', backgroundSize: '340px' }}
      />
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-64 w-[52rem] max-w-full rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, hsl(var(--gold) / 0.14) 0%, transparent 70%)' }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
          <div>
            <motion.span
              {...rise(0)}
              className="inline-block rounded-full border-2 border-gold bg-secondary px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-gold"
            >
              {g.label}
            </motion.span>
            <motion.h2
              {...rise(0.06)}
              data-testid="goldroom-title"
              className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground"
            >
              {g.title}
            </motion.h2>
            <motion.p {...rise(0.12)} className="mt-5 text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              {g.body}
            </motion.p>

            {/* Flow steps */}
            <div className="mt-10 space-y-4" data-testid="goldroom-steps">
              {g.steps.map((step, i) => (
                <motion.div
                  key={step.title}
                  {...rise(0.15 + i * 0.07)}
                  data-testid={`goldroom-step-${i}`}
                  className="sticker-frame flex items-center gap-5 rounded-2xl bg-card px-5 py-4 sm:px-6 sm:py-5"
                >
                  <span className="font-display text-2xl sm:text-3xl font-bold text-gold w-10 shrink-0" aria-hidden="true">
                    0{i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-foreground">{step.title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                  </div>
                  <img src={STEP_PROPS[i]} alt="" aria-hidden="true" className="h-10 sm:h-12 w-auto shrink-0 drop-shadow" loading="lazy" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Vault loop */}
          <motion.div
            initial={reduced ? {} : { opacity: 0, scale: 0.96 }}
            whileInView={reduced ? {} : { opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-xl"
          >
            <div className="hero-frame-gold rounded-2xl overflow-hidden bg-ink rotate-[1deg]">
              {reduced ? (
                <img
                  src="/assets/03-lore-scenes/05-gold-vault-flat.png"
                  alt={g.videoAlt}
                  className="w-full h-auto block"
                  loading="lazy"
                />
              ) : (
                <video
                  data-testid="goldroom-video"
                  className="w-full h-auto block"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster="/assets/03-lore-scenes/05-gold-vault-flat.png"
                  aria-label={g.videoAlt}
                >
                  <source src="/assets/07-motion/peipei-lore-gold-loop-1080p.mp4" type="video/mp4" />
                </video>
              )}
            </div>
            <img
              src="/assets/01-mascot/cutouts/peipei-gold-bar-flat.png"
              alt=""
              aria-hidden="true"
              className="gentle-float pointer-events-none absolute z-30 -bottom-8 -right-7 w-24 sm:-bottom-10 sm:-right-10 sm:w-36 rotate-[6deg] drop-shadow-xl"
              loading="lazy"
            />

            {/* XAUT fact plate */}
            <motion.div
              {...rise(0.2)}
              data-testid="goldroom-fact"
              className="sticker-frame relative z-10 mt-12 rounded-2xl bg-gold p-6 sm:p-7 rotate-[-1deg]"
            >
              <div className="flex items-start gap-4">
                <img src="/assets/04-props/xaut-gold-bar.png" alt="" aria-hidden="true" className="h-12 w-auto shrink-0 drop-shadow" loading="lazy" />
                <div className="min-w-0 pr-16 sm:pr-0">
                  <p className="font-display text-lg font-bold text-ink">{g.factTitle}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/80">{g.factBody}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.p {...rise(0.25)} data-testid="goldroom-honest" className="mt-14 text-sm leading-relaxed text-muted-foreground max-w-2xl">
          {g.honest}
        </motion.p>
      </div>
    </section>
  );
};
